/*
 * Kudeko · Wishlist Hero · 1.2 · 2026-10-17
 * Muestra las opciones de la variante que utiliza el botón nativo de la app.
 * Solo consultas GET al catálogo del idioma actual. Sin librerías ni cambios
 * en el guardado, el precio, los enlaces o las acciones del carrito.
 * Requiere el CSS 1.2. Instalación y retirada: ver INSTALACION.txt.
 */
(() => {
  'use strict';
  if (window.KudekoWishlistHeroVariants || !window.MutationObserver || !window.fetch) return;
  window.KudekoWishlistHeroVariants = { version: '1.2' };

  const CARD = '.wishlist-hero-list-card';
  const PRICE = '.wishlist-hero-price-update[id^="wishlist-hero-price-update-"]';
  const TITLE = '.wishlist-hero-list-card-header-product-name';
  const LABEL = 'kdk-wlh-variant';
  const portals = new Map();
  const products = new Map();
  const queue = [];
  let activeRequests = 0;
  const normal = value => String(value || '').replace(/\s+/g, ' ').trim();

  function describe(card) {
    const price = card.querySelector(PRICE);
    const match = price && /^wishlist-hero-price-update-(\d+)$/.exec(price.id);
    const host = price && price.closest('.wishlist-hero-list-card-product-price');
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!match || !host || typeof root !== 'string' || !/^\/(?:[\w-]+\/)*$/.test(root)) return null;
    const title = card.querySelector(TITLE);
    const links = [price.getAttribute('data-product-link'), title && title.getAttribute('href')];
    for (const link of links) {
      if (!link || /^(?!https?:)[a-z][a-z\d+.-]*:/i.test(link)) continue;
      const path = /(?:^|\/)products\/([^/?#]+)(?:[/?#]|$)/.exec(link);
      if (!path) continue;
      try {
        const handle = decodeURIComponent(path[1]);
        if (!handle || /[\/\\?#\u0000-\u001f]/.test(handle)) continue;
        const url = root + 'products/' + encodeURIComponent(handle) + '.js';
        return { id: match[1], url, key: url + '#' + match[1], host, title };
      } catch (_) { /* Un enlace inválido no debe afectar a la lista. */ }
    }
    return null;
  }

  function compactProduct(product) {
    if (!product || !Array.isArray(product.variants)) return null;
    const result = new Map();
    for (const variant of product.variants) {
      if (!variant || !/^\d+$/.test(String(variant.id))) continue;
      const raw = Array.isArray(variant.options)
        ? variant.options : [variant.option1, variant.option2, variant.option3];
      const options = raw.filter(value => typeof value === 'string' && value.trim()).map(value => value.trim());
      const text = options.length ? options.join(' / ') : normal(variant.public_title || variant.title);
      if (!text || text === 'Default Title') continue;
      result.set(String(variant.id), {
        text,
        names: [normal(variant.name), normal(product.title + ' - ' + variant.title)].filter(Boolean)
      });
    }
    return result;
  }

  function runQueue() {
    while (activeRequests < 3 && queue.length) {
      const task = queue.shift();
      if (![...task.entry.needed].some(check => check())) {
        if (products.get(task.url) === task.entry) products.delete(task.url);
        task.entry.needed.clear();
        task.resolve(undefined);
        continue;
      }
      activeRequests += 1;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      fetch(task.url, {
        method: 'GET', credentials: 'same-origin',
        headers: { Accept: 'application/json' }, signal: controller.signal
      })
        .then(response => response.ok ? response.json() : null)
        .then(compactProduct)
        .catch(() => null)
        .then(result => {
          task.entry.expires = result ? Infinity : Date.now() + 30000;
          task.entry.needed.clear();
          task.resolve(result);
        })
        .finally(() => {
          clearTimeout(timeout);
          activeRequests -= 1;
          // Acotar memoria sin almacenar datos de clientes ni usar localStorage.
          if (products.size > 80) {
            for (const [url, entry] of products) {
              if (products.size <= 80) break;
              if (entry.expires !== 0) products.delete(url);
            }
          }
          runQueue();
        });
    }
  }

  function getProduct(url, needed) {
    const cached = products.get(url);
    if (cached && (!cached.expires || cached.expires > Date.now())) {
      if (!cached.expires) cached.needed.add(needed);
      return cached.promise;
    }
    const entry = { expires: 0, promise: null, needed: new Set([needed]) };
    entry.promise = new Promise(resolve => queue.push({ url, entry, resolve }));
    products.set(url, entry);
    runQueue();
    return entry.promise;
  }

  function isCurrent(entry, card, state) {
    if (!entry.root.isConnected || !entry.paper || !entry.paper.contains(card) || entry.states.get(card) !== state) return false;
    const description = describe(card);
    return !!description && description.key === state.key;
  }

  function putLabel(state, description) {
    if (!state.variant) return;
    // Algunas entradas guardadas desde una ficha ya incluyen la variante en
    // su nombre. Solo se omite la repetición si coincide el nombre completo.
    if (state.variant.names.includes(normal(description.title && description.title.textContent))) {
      if (state.label) state.label.remove();
      return;
    }
    if (!state.label) {
      state.label = document.createElement('div');
      state.label.className = LABEL;
      state.label.style.display = 'none'; // El CSS 1.2 activa la presentación.
      state.label.textContent = state.variant.text;
      state.label.setAttribute('data-kdk-wlh-variant-id', description.id);
    }
    if (state.label.parentElement !== description.host) description.host.prepend(state.label);
  }

  function loadCard(entry, card) {
    const state = entry.states.get(card);
    if (!state || state.requested || !isCurrent(entry, card, state)) return;
    const description = describe(card);
    if (!description || description.key !== state.key) return;
    state.requested = true;
    if (entry.intersections) entry.intersections.unobserve(card);
    getProduct(description.url, () => isCurrent(entry, card, state) && entry.paper.getClientRects().length > 0).then(variants => {
      if (!isCurrent(entry, card, state)) return;
      if (variants === undefined) { state.requested = false; return; }
      const current = describe(card);
      if (!current || current.key !== state.key) return;
      // Nunca se sustituye una variante ausente por variants[0].
      state.variant = variants && variants.get(description.id);
      putLabel(state, current);
    });
  }

  function scan(entry) {
    entry.frame = 0;
    if (!entry.root.isConnected) return;
    const header = entry.root.querySelector('.MuiDialog-paper > .wishlist-hero-header');
    const paper = header && header.parentElement;
    if (!paper || getComputedStyle(paper).getPropertyValue('--kdk-wlh-variant-enabled').trim() !== '1') return;
    if (paper !== entry.paper) {
      if (entry.intersections) entry.intersections.disconnect();
      entry.paper = paper;
      entry.cards = new Set();
      entry.states = new WeakMap();
      entry.intersections = window.IntersectionObserver ? new IntersectionObserver(changes => {
        for (const change of changes) if (change.isIntersecting) loadCard(entry, change.target);
      }, { root: paper.querySelector('.MuiDialogContent-root') || paper, rootMargin: '160px 0px' }) : null;
    }
    const current = new Set(paper.querySelectorAll(CARD));
    for (const previous of entry.cards) {
      if (!current.has(previous) && entry.intersections) entry.intersections.unobserve(previous);
    }
    entry.cards = current;
    for (const card of current) {
      const description = describe(card);
      let state = entry.states.get(card);
      if (!description) {
        card.querySelectorAll('.' + LABEL).forEach(label => label.remove());
        entry.states.delete(card);
        continue;
      }
      if (!state || state.key !== description.key) {
        card.querySelectorAll('.' + LABEL).forEach(label => label.remove());
        state = { key: description.key, requested: false, variant: null, label: null };
        entry.states.set(card, state);
      }
      putLabel(state, description);
      if (!state.requested) {
        if (entry.intersections) entry.intersections.observe(card);
        else loadCard(entry, card);
      }
    }
  }

  function schedule(entry) {
    if (!entry.frame) entry.frame = requestAnimationFrame(() => scan(entry));
  }

  function watch(root) {
    if (portals.has(root)) { schedule(portals.get(root)); return; }
    const entry = { root, paper: null, frame: 0, intersections: null, cards: new Set(), states: new WeakMap() };
    entry.observer = new MutationObserver(records => {
      const relevant = records.some(record => {
        if (record.type === 'attributes') return record.target.matches('.wishlist-hero-price-update,' + TITLE);
        const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        if (target && target.closest('.' + LABEL)) return false;
        if (record.type === 'characterData') return !!(target && target.closest(TITLE));
        // Reponer una etiqueta retirada por un render de la app, sin volver
        // a escanear por la inserción de nuestras propias etiquetas.
        if (record.removedNodes.length) return true;
        return [...record.addedNodes].some(node =>
          node.nodeType !== 1 || !node.classList.contains(LABEL));
      });
      if (relevant) schedule(entry);
    });
    entry.observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['id', 'data-product-link', 'href'] });
    portals.set(root, entry);
    schedule(entry);
  }

  function findPortals(node) {
    if (node.nodeType !== 1) return;
    if (node.matches('.MuiDialog-root')) watch(node);
    node.querySelectorAll('.MuiDialog-root').forEach(watch);
  }

  function cleanup() {
    for (const [root, entry] of portals) {
      if (root.isConnected) continue;
      entry.observer.disconnect();
      if (entry.intersections) entry.intersections.disconnect();
      if (entry.frame) cancelAnimationFrame(entry.frame);
      portals.delete(root);
    }
  }

  function start() {
    findPortals(document.body);
    // Solo hijos directos de body; el observer profundo se limita al portal.
    new MutationObserver(records => {
      cleanup();
      for (const record of records) record.addedNodes.forEach(findPortals);
    }).observe(document.body, { childList: true });
    for (const event of ['wishlist-hero-open-popup', 'wishlist-hero-wishlist-show', 'wishlist-hero-wishlist-updated']) {
      document.addEventListener(event, () => { cleanup(); findPortals(document.body); });
      window.addEventListener(event, () => { cleanup(); findPortals(document.body); });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
